import { marked } from 'marked'
import hljs from 'highlight.js'
import DOMPurify from 'dompurify'

/**
 * Progressive Markdown Renderer Hook
 *
 * Renders markdown progressively during streaming to avoid the "jump" when
 * formatting is applied only after streaming completes.
 *
 * Uses phx-update="ignore" to maintain ownership of inner DOM while LiveView
 * manages the data attributes. This is the recommended LiveView pattern for
 * client-side rendering.
 *
 * Performance: Updates to data-content attribute are cheap (no reflow), and
 * rendering is debounced during streaming to avoid excessive re-renders.
 *
 * Data attributes:
 * - data-content: The raw markdown content (updated by LiveView)
 * - data-is-streaming: Whether the message is currently streaming
 */
const MarkdownRenderer = {
  mounted () {
    this.configureMarked()
    this.renderTimer = null
    this.render()
  },

  updated () {
    // LiveView updated the data-content attribute
    // Debounce rendering during streaming to avoid excessive re-renders
    const isStreaming = this.el.dataset.isStreaming === 'true'

    if (isStreaming) {
      // During streaming, debounce to max 50ms between renders
      clearTimeout(this.renderTimer)
      this.renderTimer = setTimeout(() => this.render(), 50)
    } else {
      // When streaming completes, render immediately
      clearTimeout(this.renderTimer)
      this.render()
    }
  },

  destroyed () {
    clearTimeout(this.renderTimer)
  },

  configureMarked () {
    // Configure marked for GitHub-flavored markdown
    // Keep it simple - let marked handle rendering and use Tailwind prose for styling
    marked.setOptions({
      gfm: true, // GitHub Flavored Markdown
      breaks: true, // Convert \n to <br>
      headerIds: false, // Don't generate header IDs
      mangle: false // Don't escape email addresses
    })
  },

  render () {
    const content = this.el.dataset.content || ''
    const isStreaming = this.el.dataset.isStreaming === 'true'

    if (!content) {
      this.el.innerHTML = ''
      return
    }

    try {
      // Parse markdown to HTML
      const html = marked.parse(content)

      // Sanitize HTML to prevent XSS attacks
      const sanitizedHtml = DOMPurify.sanitize(html)

      // Update the inner HTML (safe because of phx-update="ignore")
      this.el.innerHTML = `<div class="prose prose-sm max-w-none dark:prose-invert">${sanitizedHtml}</div>`

      // Apply styling to lists and code blocks
      this.applyPostRenderStyling()

      // Only apply syntax highlighting when streaming is complete
      // This avoids performance issues from re-highlighting on every update
      if (!isStreaming) {
        this.applySyntaxHighlighting()
      }
    } catch (error) {
      console.error('Markdown rendering error:', error)
      // Fallback to plain text
      this.el.innerHTML = `<div class="prose prose-sm max-w-none dark:prose-invert"><p>${this.escapeHtml(content)}</p></div>`
    }
  },

  applyPostRenderStyling () {
    // Force all links in rendered markdown to open in a new tab. Runs here
    // (not inside marked) because DOMPurify strips target/rel by default;
    // reapplying post-sanitise is safer than relaxing the sanitiser. Also
    // tag each anchor with data-wavebox-link so the global click listener in
    // app.js routes the click through the Wavebox extension (current
    // Space/partition), falling back to window.open on timeout/error.
    this.el.querySelectorAll('a[href]').forEach((a) => {
      a.setAttribute('target', '_blank')
      a.setAttribute('rel', 'noopener noreferrer')
      a.setAttribute('data-wavebox-link', 'true')
    })

    // Add explicit list styling classes to override CSS reset
    this.el.querySelectorAll('ul').forEach((ul) => {
      ul.classList.add('list-disc', 'list-outside', 'ml-6', 'space-y-1', 'my-2')
    })

    this.el.querySelectorAll('ol').forEach((ol) => {
      ol.classList.add('list-decimal', 'list-outside', 'ml-6', 'space-y-1', 'my-2')
    })

    // Add classes to code blocks for syntax highlighting
    this.el.querySelectorAll('pre code').forEach((block) => {
      block.classList.add('language-code')
    })

    // Ensure pre blocks have relative positioning and add copy buttons
    this.el.querySelectorAll('pre').forEach((pre) => {
      if (!pre.style.position) {
        pre.style.position = 'relative'
      }

      // Add copy button if not already present
      if (!pre.querySelector('.code-copy-btn')) {
        this.addCopyButton(pre)
      }
    })
  },

  addCopyButton (preElement) {
    const button = document.createElement('button')
    button.className = 'code-copy-btn'
    button.type = 'button'
    button.setAttribute('aria-label', 'Copy code to clipboard')
    button.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5A3.375 3.375 0 006.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0015 2.25h-1.5a2.251 2.251 0 00-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 00-9-9z" />
      </svg>
    `

    button.addEventListener('click', () => {
      const code = preElement.querySelector('code')
      const text = code ? code.textContent : preElement.textContent

      navigator.clipboard.writeText(text).then(() => {
        // Show success feedback
        const originalHTML = button.innerHTML
        button.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        `

        setTimeout(() => {
          button.innerHTML = originalHTML
        }, 2000)
      }).catch((err) => {
        console.error('Failed to copy code:', err)
      })
    })

    preElement.appendChild(button)
  },

  applySyntaxHighlighting () {
    // Apply syntax highlighting to all code blocks
    // Only called when streaming is complete to avoid performance issues
    this.el.querySelectorAll('pre code').forEach((block) => {
      // Check if already highlighted to avoid re-highlighting
      if (!block.dataset.highlighted) {
        hljs.highlightElement(block)
        block.dataset.highlighted = 'true'
      }
    })
  },

  escapeHtml (text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}

export default MarkdownRenderer
